import { useQuery } from "@tanstack/react-query";
import { PROOFING_FLOW_APP_ID } from "@/config";
import {
  projectsGetProjectEntities,
  projectsGetProjects,
  projectsGetProjectUsersByApp,
} from "@/usersAndProjects-client/sdk.gen";
import type { Project, ProjectSupplier } from "../entities";

/**
 * Fetch projects filtered by tenant
 */
export interface UseFetchProjectsByTenantResult {
  projects: Project[] | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchProjectsByTenant = (
  tenantId: string | null | undefined,
): UseFetchProjectsByTenantResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["projects", "tenant", tenantId],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error("Tenant ID is required");
      }
      const response = await projectsGetProjects({
        query: { tenantId },
      });
      return response.data;
    },
    enabled: !!tenantId,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    projects: data || null,
    isLoading,
    error: error as Error | null,
  };
};

/**
 * Fetch suppliers (groups with supplier role) for a specific project
 */
export interface UseFetchSuppliersByProjectResult {
  suppliers: ProjectSupplier[] | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchSuppliersByProject = (
  projectId: string | null | undefined,
): UseFetchSuppliersByProjectResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["suppliers", "project", projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      const response = await projectsGetProjectEntities({
        path: { projectId, roleName: "supplier" },
      });
      // Filter and map to ProjectSupplier type (entities with entityType "group")
      return (
        response.data
          ?.filter((entity) => entity.entityType === "group")
          .map((entity) => ({
            id: entity.id,
            name: entity.name,
          })) || []
      );
    },
    enabled: !!projectId,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes per project
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    suppliers: data || null,
    isLoading,
    error: error as Error | null,
  };
};

/**
 * Fetch users assigned to a specific project
 */
export interface ProjectUser {
  id: string;
  name: string | null;
  email: string | null;
  role: {
    name: string;
    scope: "app" | "project";
  };
}

export interface UseFetchUsersByProjectResult {
  users: ProjectUser[] | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchUsersByProject = (
  projectId: string | null | undefined,
): UseFetchUsersByProjectResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["users", "project", projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      const response = await projectsGetProjectUsersByApp({
        path: { projectId, appId: PROOFING_FLOW_APP_ID },
      });
      // Map to ProjectUser type
      return (
        response.data.map((user) => ({
          id: user.sub,
          name: user.displayName,
          email: user.mail,
          role: user.role,
        })) || []
      );
    },
    enabled: !!projectId,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes per project
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    users: data || null,
    isLoading,
    error: error as Error | null,
  };
};
